import { useMemo, useState, useEffect } from "react";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ListFilter, Search as SearchIcon } from "lucide-react";
import { DetailHeader } from "@/components/layout/detail-header";
import { useToast } from "@/contexts/toast-context";
import { Combobox } from "@/components/ui/combobox";
// Using manual table markup here to ensure horizontal scroll syncs with sticky header

type ServerHost = "adp.flatfile.com" | "qa.adp.flatfile.com";

interface CommitItem {
  id?: string;
  commitId?: string;
  createdAt?: string;
  label?: string;
  message?: string;
}

interface RecordsResponse {
  data?: {
    success?: boolean;
    records?: Array<{
      id: string;
      values: Record<string, { value: any; valid?: boolean; updatedAt?: string }>;
    }>;
  };
  records?: any[]; // fallback
}

export default function CommitsPage() {
  const { showError } = useToast();
  const [server, setServer] = useState<ServerHost>("adp.flatfile.com");
  const [sheetId, setSheetId] = useState("");
  const [token, setToken] = useState("");
  const [isLoadingCommits, setIsLoadingCommits] = useState(false);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [commits, setCommits] = useState<CommitItem[]>([]);
  const [selectedCommit, setSelectedCommit] = useState<CommitItem | null>(null);
  const [records, setRecords] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCols, setVisibleCols] = useState<Record<string, boolean>>({});
  const [columnSearch, setColumnSearch] = useState("");
  const [commitPage, setCommitPage] = useState(1);
  const commitPageSize = 15;

  const apiBase = useMemo(() => `https://${server}/api/v1`, [server]);

  function authHeader(tokenValue: string) {
    return tokenValue.startsWith("Bearer ") ? tokenValue : `Bearer ${tokenValue}`;
  }

  async function fetchJson(url: string) {
    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": authHeader(token),
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText}${text ? `: ${text}` : ""}`);
    }
    return res.json();
  }

  async function loadCommits() {
    if (!sheetId || !token) return;
    setIsLoadingCommits(true);
    
    setSelectedCommit(null);
    setRecords([]);
    try {
      const url = `${apiBase}/sheets/${encodeURIComponent(sheetId)}/commits?completed=true`;
      const data = await fetchJson(url);
      const list: CommitItem[] = Array.isArray(data)
        ? data
        : data.data || data.items || data.commits || [];
      setCommits(list);
      setCommitPage(1);
    } catch (e: any) {
      showError("Couldn't load commits. Please check the server, sheet ID, and token.", "Failed to load commits");
      setCommits([]);
    } finally {
      setIsLoadingCommits(false);
    }
  }

  async function loadRecords(commitId: string) {
    if (!sheetId || !token) return;
    setIsLoadingRecords(true);
    
    try {
      const url = `${apiBase}/sheets/${encodeURIComponent(sheetId)}/records?commitId=${encodeURIComponent(commitId)}`;
      const data: RecordsResponse = await fetchJson(url);
      const recs = data?.data?.records || data.records || [];
      setRecords(recs);
    } catch (e: any) {
      showError("Couldn't load records for this commit. Please try again.", "Failed to load records");
      setRecords([]);
    } finally {
      setIsLoadingRecords(false);
    }
  }
    
  // Compute dynamic columns: include only fields that have at least one non-empty value
  const baseColumns = useMemo(() => {
    const fieldSet = new Set<string>();
    for (const r of records) {
      const values = r?.values || {};
      for (const [key, meta] of Object.entries(values)) {
        const hasValue = meta && (meta as any).value !== undefined && (meta as any).value !== null && `${(meta as any).value}` !== "";
        if (hasValue) fieldSet.add(key);
      }
    }
    return Array.from(fieldSet);
  }, [records]);

  // Keep visibility map in sync with available columns
  useEffect(() => {
    if (baseColumns.length === 0) {
      setVisibleCols({});
      return;
    }
    setVisibleCols((prev) => {
      const next: Record<string, boolean> = { ...prev };
      let changed = false;
      for (const key of baseColumns) {
        if (next[key] === undefined) { next[key] = true; changed = true; }
      }
      // Remove keys that no longer exist
      for (const k of Object.keys(next)) {
        if (!baseColumns.includes(k)) { delete next[k]; changed = true; }
      }
      return changed ? { ...next } : next;
    });
  }, [baseColumns]);

  const effectiveColumnKeys = useMemo(() => baseColumns.filter((k) => visibleCols[k] !== false), [baseColumns, visibleCols]);
  const filteredColumnKeys = useMemo(() => {
    const q = columnSearch.trim().toLowerCase();
    if (!q) return baseColumns;
    return baseColumns.filter(k => k.toLowerCase().includes(q));
  }, [baseColumns, columnSearch]);

  const tableColumns = useMemo(() => {
    return effectiveColumnKeys.map((key) => ({
      key,
      header: key,
      width: 48,
      className: "whitespace-nowrap",
      render: (row: any) => {
        const cell = row?.values?.[key];
        const value = cell?.value ?? "";
        return <span className="truncate block">{String(value)}</span>;
      },
    }));
  }, [effectiveColumnKeys]);

  const filteredRecords = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return records;
    return records.filter((row) => {
      for (const key of effectiveColumnKeys) {
        const v = row?.values?.[key]?.value;
        if (v !== undefined && v !== null && String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [records, searchTerm, effectiveColumnKeys]);

  const sortedCommits = useMemo(() => {
    const copy = [...commits];
    copy.sort((a: any, b: any) => {
      const ad = new Date(a.createdAt || a.created_at || a.timestamp || 0).getTime();
      const bd = new Date(b.createdAt || b.created_at || b.timestamp || 0).getTime();
      return bd - ad;
    });
    return copy;
  }, [commits]);

  // No-op effect removed to satisfy linter warning about unnecessary dependencies

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={[{ label: "Commit" }]}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search records..."
        actions={(
          <DropdownMenu
            trigger={<Button variant="secondary" size="sm" className="h-9 px-3 text-xs"><ListFilter className="w-3 h-3 mr-2" /> Columns</Button>}
            align="right"
            side="bottom"
            triggerAsChild
          >
            <div className="p-2 bg-background border-b border-border">
              <div className="relative">
                <input
                  placeholder="Search columns"
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-border bg-background"
                  value={columnSearch}
                  onChange={(e) => setColumnSearch(e.target.value)}
                />
                <SearchIcon className="w-4 h-4 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
              </div>
            </div>
            <div className="px-3 py-1 flex items-center justify-end gap-3 text-[11px] text-muted-foreground border-b border-border">
              <button className="hover:text-foreground cursor-pointer" onClick={() => setVisibleCols(Object.fromEntries(baseColumns.map(k => [k, true])))}>Select all</button>
              <span className="text-border">|</span>
              <button className="hover:text-foreground cursor-pointer" onClick={() => setVisibleCols(Object.fromEntries(baseColumns.map(k => [k, false])))}>Clear all</button>
            </div>
            <div className="max-h-64 overflow-auto">
              {filteredColumnKeys.map((k) => (
                <DropdownMenuItem key={k} className="text-xs" onClick={() => setVisibleCols(v => ({ ...v, [k]: !(v[k] !== false) }))}>
                  <input type="checkbox" readOnly checked={visibleCols[k] !== false} className="mr-2 shrink-0" />
                  <span className="truncate block">{k}</span>
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenu>
        )}
      />

      <div className="flex-1 overflow-hidden min-h-0">
        <div className="flex h-full min-h-0">
        {/* Left panel / sub-sidebar */}
        <aside className="w-80 min-w-80 max-w-80 border-r border-border flex flex-col">
        <div className="p-4 border-b border-border sticky top-0 bg-background z-10 space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Server</label>
            <Combobox
              value={server}
              onChange={(val) => setServer((val as ServerHost) || "adp.flatfile.com")}
              options={[
                { value: "adp.flatfile.com", label: "adp.flatfile.com", description: "Production" },
                { value: "qa.adp.flatfile.com", label: "qa.adp.flatfile.com", description: "QA" },
              ]}
              placeholder="Select server..."
              className="text-xs"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Sheet ID</label>
            <input
              className="w-full px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="sh_..."
              value={sheetId}
              onChange={(e) => setSheetId(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Access token</label>
            <input
              className="w-full px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Bearer ..."
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={loadCommits}
              disabled={!sheetId || !token || isLoadingCommits}
              size="sm"
              className="text-xs h-9 cursor-pointer"
            >
              {isLoadingCommits ? "Loading..." : "Load commits"}
            </Button>
          </div>
          {/* Errors are handled via toast notifications */}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingCommits ? (
            <div className="h-full flex h-full items-center justify-center text-xs text-muted-foreground">Loading...</div>
          ) : commits.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground h-full flex items-center justify-center">{sheetId && token ? "No commits" : "Enter sheet and token"}</div>
          ) : (
            <div className="py-2 space-y-2">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground px-3">
                <div>
                  {(() => {
                    const total = commits.length;
                    const pages = Math.max(1, Math.ceil(total / commitPageSize));
                    return `Page ${commitPage} of ${pages}`;
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="h-7 text-[11px] px-2 cursor-pointer" variant="secondary" disabled={commitPage <= 1} onClick={() => setCommitPage((p) => Math.max(1, p - 1))}>Prev</Button>
                  <Button size="sm" className="h-7 text-[11px] px-2 cursor-pointer" variant="secondary" onClick={() => {
                    const total = commits.length;
                    const pages = Math.ceil(total / commitPageSize) || 1;
                    setCommitPage((p) => Math.min(pages, p + 1));
                  }}>Next</Button>
                </div>
              </div>

              <div className="divide-y divide-border border-b border-t border-border">
                {sortedCommits
                  .slice((commitPage - 1) * commitPageSize, (commitPage - 1) * commitPageSize + commitPageSize)
                  .map((c) => {
                  const id = c.id || c.commitId || "";
                  const created = c.createdAt || "";
                  const isSelected = (selectedCommit?.id || selectedCommit?.commitId) === (c.id || c.commitId);
                  return (
                    <button
                      key={id}
                      className={`w-full text-left p-3 hover:bg-muted/50 cursor-pointer ${isSelected ? "bg-muted" : ""}`}
                      onClick={() => { setSelectedCommit(c); loadRecords(id); }}
                    >
                      <div className="text-xs font-medium truncate">{id}</div>
                      <div className="text-[10px] text-muted-foreground">{created}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Right pane */}
      <section className="flex-1 min-h-0 overflow-hidden">
        {!selectedCommit ? (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Select a commit to view records</div>
        ) : (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-border p-3">
              <div className="text-sm font-medium truncate">{selectedCommit.id || selectedCommit.commitId}</div>
              <div className="text-[10px] text-muted-foreground truncate">{selectedCommit.createdAt || ""}</div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              {isLoadingRecords ? (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Loading...</div>
              ) : filteredRecords.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No records</div>
              ) : (
                <table className="w-max min-w-full table-auto border-separate border-spacing-0 relative">
                  <thead className="sticky top-0 z-20 bg-[#fafafa] border-b border-border">
                    <tr>
                      {tableColumns.map((col) => (
                        <th
                          key={String(col.key)}
                          className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap min-w-40 border-b border-border border-r last:border-r-0"
                        >
                          <span className="truncate block pr-1">{col.header}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((row: any, idx: number) => (
                      <tr key={row.id || idx} className="hover:bg-muted/50">
                        {tableColumns.map((col) => (
                          <td key={String(col.key)} className="px-4 py-2 text-xs align-top min-w-40 border-b border-border border-r last:border-r-0">
                            <div className="truncate max-w-[28rem]">{(col as any).render ? (col as any).render(row) : (row as any)[col.key as any]}</div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </section>

        {/* Close main content row (sidebar + right pane) */}
        </div>
      {/* Close scroll wrapper */}
      </div>

      {/* Invisible click layer for commit rows */}
      <div className="hidden" />
    </div>
  );
}


